import { defineStore } from 'pinia';
import { ref } from 'vue';
import { type AppLocale, RTL_LOCALES, SUPPORTED_LOCALES } from '../i18n.js';

const STORAGE_KEY = 'jervis.locale';

function isAppLocale(value: unknown): value is AppLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** Saved choice → browser language → Arabic (first customer is Egyptian). */
export function detectInitialLocale(): AppLocale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isAppLocale(saved)) {
      return saved;
    }
  } catch {
    // Storage can be unavailable (private mode, policy); fall through.
  }
  const browser = globalThis.navigator?.language?.slice(0, 2);
  return isAppLocale(browser) ? browser : 'ar';
}

export function applyDocumentLocale(locale: AppLocale): void {
  document.documentElement.lang = locale;
  document.documentElement.dir = RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
}

/** Per-user UI preferences (not business data). */
export const usePreferencesStore = defineStore('preferences', () => {
  const locale = ref<AppLocale>(detectInitialLocale());

  function setLocale(next: AppLocale): void {
    locale.value = next;
    applyDocumentLocale(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Non-critical: preference simply won't persist.
    }
  }

  return { locale, setLocale };
});
